import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { addDays, startOfDay } from 'date-fns'
import { groupTasksByDue } from '../utils/groupTasksByDue'
import { ListDetailsView } from './ListDetailsView'
import type { ListDetailsViewSection } from './ListDetailsView'
import type { TaskViewContext } from './TaskPageLayout'
import { useTaskMutations } from '@/lib/db/rxdb/hooks'

/** Due date a task added from a group's + gets, in days from today (null = none). */
const ADD_OFFSET: Partial<Record<string, number | null>> = { today: 0, tomorrow: 1, noDate: null }

/** Long groups collapse after this many rows (design 13a). */
const COLLAPSE_AFTER = 8

/**
 * Inbox as a triage list (design 13a): grouped Overdue → Today → Tomorrow →
 * Upcoming → No date, long groups collapsed, and one click to move every
 * overdue task to today.
 */
export function DueGroupedTaskView(context: TaskViewContext) {
  const { t } = useTranslation('tasks')
  const { updateTask } = useTaskMutations()

  const sections = useMemo<Array<ListDetailsViewSection>>(() => {
    return groupTasksByDue(context.items).map((group) => ({
      id: `due-${group.id}`,
      title: t(`grouping.${group.id}`),
      items: group.tasks,
      collapsible: true,
      defaultOpen: true,
      collapseAfter: COLLAPSE_AFTER,
      onAdd: context.openAddTask && ADD_OFFSET[group.id] !== undefined
        ? () => {
            const offset = ADD_OFFSET[group.id]
            context.openAddTask?.({
              dueDate: offset === null || offset === undefined ? null : addDays(startOfDay(new Date()), offset),
            })
          }
        : undefined,
      headerAction:
        group.id === 'overdue' ? (
          <button
            type="button"
            onClick={() => {
              const today = startOfDay(new Date()).getTime()
              for (const task of group.tasks) void updateTask(task.id, { dueDate: today, dueDateType: 'date' })
            }}
            className="rounded-md bg-brand-tint px-2 py-0.5 text-[11.5px] font-medium text-brand-tint-foreground transition-opacity hover:opacity-80"
          >
            {t('grouping.rescheduleAll')}
          </button>
        ) : undefined,
    }))
  }, [context.items, context.openAddTask, t, updateTask])

  return <ListDetailsView {...context} sections={sections} />
}
